import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { simulateContract, writeContract } from '@wagmi/core';

import { wagmiConfig } from '@shared/common/providers/Web3Provider/wagmi';
import { DEFAULT_TARGET_CHAIN, namedWeb3Errors, TokenName, tokensInfo } from '@shared/constants';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { useApproveIfNeeded } from '@shared/helpers/web3/erc20';
import { numberToWei } from '@shared/helpers/web3/formatters';
import { getEstimateGasPrice } from '@shared/helpers/web3/getEstimateGasPrice';
import getWalletActionStyles from '@shared/helpers/web3/getWalletActionStyles';
import { handleTransactionResult } from '@shared/helpers/web3/handleTransactionResult';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';

import { PredictionDirection } from '../../model/PredictionDirection';
import { PREDICTIONS_CONTRACT } from '../config/contract';
import { getQueryKey } from '../config/query';

type Props = {
  amount: number;
  direction: PredictionDirection;
  tokenName?: TokenName;
};

const DEFAULT_TOKEN_NAME: TokenName = 'USDT';

export const usePredictionBet = () => {
  const { approveIfNeeded } = useApproveIfNeeded();
  const { getUserWalletType } = useMagicIdUserWalletInfo();
  const queryClient = useQueryClient();

  const approve = async ({
    tokenName = DEFAULT_TOKEN_NAME,
    amount,
  }: Pick<Props, 'amount' | 'tokenName'>) => {
    const { decimals, contractsAddresses } = tokensInfo[tokenName];

    try {
      await approveIfNeeded({
        chainId: DEFAULT_TARGET_CHAIN.id,
        amount: Number.MAX_SAFE_INTEGER,
        spenderAddress: PREDICTIONS_CONTRACT.address,
        tokenAddress: contractsAddresses.token,
        tokenDecimals: decimals,
        amountToCheck: amount,
      });
      return true;
    } catch (error) {
      displayWalletErrorToast({
        error,
      });
      return false;
    }
  };

  const estimateGasPrice = async ({ amount, direction, tokenName = DEFAULT_TOKEN_NAME }: Props) => {
    const { userWallet } = getUserWalletType();
    const { decimals } = tokensInfo[tokenName];

    const preparedAmount = numberToWei(amount, decimals).toBigInt();

    try {
      const estimatedGasPrice = await getEstimateGasPrice({
        abi: PREDICTIONS_CONTRACT.abi,
        contractAddress: PREDICTIONS_CONTRACT.address,
        functName: 'bet',
        chain: DEFAULT_TARGET_CHAIN,
        walletAddress: userWallet.attributes.address,
        args: [preparedAmount, direction === PredictionDirection.UP ? 0 : 1],
      });
      return estimatedGasPrice;
    } catch (error) {
      console.error('error = ', error);
      displayWalletErrorToast({
        error,
        unknownExceptionReplaceMessage: namedWeb3Errors.UNKNOWN_TRANSACTION_ERROR,
      });
      return null;
    }
  };

  const betPrediction = async ({ amount, direction, tokenName = DEFAULT_TOKEN_NAME }: Props) => {
    if (!(await approve({ amount, tokenName }))) {
      return;
    }

    const { walletType } = getUserWalletType();
    const toastId = Date.now();

    toast.loading('Processing your prediction...', {
      toastId,
      style: getWalletActionStyles(walletType),
    });

    const estimatedGasInfo = await estimateGasPrice({ amount, direction, tokenName });

    if (!estimatedGasInfo) {
      return;
    }

    const { gasPrice, gasEstimate } = estimatedGasInfo;
    const { decimals } = tokensInfo[tokenName];
    const preparedAmount = numberToWei(amount, decimals).toBigInt();

    try {
      const { request } = await simulateContract(wagmiConfig, {
        ...PREDICTIONS_CONTRACT,
        functionName: 'bet',
        chainId: DEFAULT_TARGET_CHAIN.id,
        args: [preparedAmount, direction === PredictionDirection.UP ? 0 : 1],
        gas: gasEstimate,
        ...(gasPrice && { gasPrice }),
      });

      const hash = await writeContract(wagmiConfig, request);
      const confirmations = 2;

      const result = await handleTransactionResult(hash, DEFAULT_TARGET_CHAIN, confirmations);

      void queryClient.invalidateQueries({
        queryKey: getQueryKey(),
      });

      return result;
    } catch (error) {
      console.error('error = ', error);
      displayWalletErrorToast({
        error,
        unknownExceptionReplaceMessage: namedWeb3Errors.UNKNOWN_TRANSACTION_ERROR,
      });
    } finally {
      toast.dismiss(toastId);
    }
  };

  return { betPrediction };
};
